import { Email } from './email.model';

/**
 * User-controlled email notification preferences.
 *
 * A missing key (or a missing object entirely) means the email is enabled —
 * existing users stay opted in without any migration, and only an explicit
 * `false` disables an email.
 *
 * Transactional emails (receipts, refunds, welcome, password reset), correction
 * notifications and all internal/admin emails are intentionally not represented
 * here - they are always sent.
 */
export interface EmailPreferences {
  /** Sent when every statement in an upload batch converts successfully. */
  conversionComplete?: boolean;
}

interface HasEmailPreferences {
  emailPreferences?: EmailPreferences;
}

export const isEmailEnabled = (user: HasEmailPreferences, key: keyof EmailPreferences): boolean =>
  user?.emailPreferences?.[key] !== false;

/**
 * Applies a user's email preference to an outgoing email.
 *
 * If the user has opted out, the email is redirected to its BCC recipients
 * (back office) instead of being dropped, so internal visibility is unchanged.
 * Returns null when there is no one left to send to (e.g. non-prod, where the
 * back office BCC is empty).
 */
export function applyEmailPreference(email: Email, user: HasEmailPreferences, key: keyof EmailPreferences): Email | null {
  if (isEmailEnabled(user, key)) {
    return email;
  }
  if (!email.bcc) {
    return null;
  }
  return { ...email, to: email.bcc, bcc: undefined };
}

/**
 * Request payload for the email-sendEmail cloud function.
 *
 * Tagging an email with `preference` makes the relay enforce that user's email
 * preferences (via applyEmailPreference) before sending. Untagged emails are
 * sent as-is - admin and transactional emails should stay untagged.
 */
export type SendEmailRequest = Email & { preference?: EmailPreferenceRef };

export interface EmailPreferenceRef {
  /** The user whose preferences gate this email. */
  userUid: string;
  key: keyof EmailPreferences;
}

/** Response of the email-sendEmail cloud function. */
export interface SendEmailResult {
  /** 'redirected' = recipient opted out, email went to the BCC (back office) only. */
  result: 'sent' | 'redirected' | 'skipped';
}
